import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { isTerminalJobState } from '@devrobot/shared';
import type {
  AuditEventDTO,
  BackupDTO,
  BranchDTO,
  EnvironmentConfigDTO,
  EnvironmentDTO,
  JobDTO,
  JobLogDTO,
  JobSummaryDTO,
  LogsResult,
  Manifest,
  MigrationDTO,
  MigrationPlanDTO,
  MigrationRequest,
  MigrationSubmitRequest,
  MetaDTO,
  OverviewDTO,
  PlatformSettingsDTO,
  ProjectDTO,
  ReleaseDTO,
  SessionDTO,
  SharedValueDTO,
  ShellSessionDTO,
  TargetDTO,
  UserDTO,
  VpsDTO,
} from '@devrobot/shared';
import { ApiError, api, seg } from './client';

export interface MeResponse {
  user: UserDTO;
  session: { id: string; kind: 'web' | 'cli'; expiresAt: string };
}

export interface JobFilters {
  active?: boolean;
  kind?: string;
  state?: string;
  target?: string;
  vps?: string;
  project?: string;
  limit?: number;
}

export interface DeploymentFilters {
  project?: string;
  vps?: string;
  environment?: string;
  all?: boolean;
}

export interface MigrationFilters {
  project?: string;
  state?: string;
}

export interface AuditFilters {
  project?: string;
  action?: string;
  actor?: string;
  vps?: string;
}

export const qk = {
  me: ['me'] as const,
  meta: ['meta'] as const,
  overview: ['overview'] as const,
  projects: ['projects'] as const,
  project: (key: string) => ['project', key] as const,
  branches: (key: string) => ['project', key, 'branches'] as const,
  environments: (key: string) => ['project', key, 'environments'] as const,
  targets: (key: string) => ['project', key, 'targets'] as const,
  releases: (key: string) => ['project', key, 'releases'] as const,
  projectJobs: (key: string) => ['project', key, 'jobs'] as const,
  manifest: (key: string) => ['project', key, 'manifest'] as const,
  config: (key: string, env: string) => ['project', key, 'config', env] as const,
  deployments: (f: DeploymentFilters = {}) => ['deployments', f] as const,
  target: (id: string) => ['target', id] as const,
  backups: (id: string) => ['target', id, 'backups'] as const,
  jobs: (f: JobFilters) => ['jobs', f] as const,
  job: (id: string) => ['job', id] as const,
  childJobs: (parentJobId: string) => ['jobs', { parent: parentJobId }] as const,
  migrations: (f: MigrationFilters) => ['migrations', f] as const,
  migration: (id: string) => ['migration', id] as const,
  vpsList: ['vps'] as const,
  vps: (id: string) => ['vps', id] as const,
  // Under ['vps'] so the `vps` change event (sent when sessions open or close) refreshes it.
  shellSessions: (vpsId: string, active: boolean) => ['vps', vpsId, 'shell-sessions', active ? 'active' : 'all'] as const,
  shared: ['shared'] as const,
  audit: (f: AuditFilters) => ['audit', f] as const,
  settings: ['settings'] as const,
  // The sidebar's pinned application hosts. A view over targets and projects, so target and
  // project change events invalidate it too (see invalidationsFor).
  shortcuts: ['shortcuts'] as const,
  sessions: ['sessions'] as const,
  // Everything notification-related sits under ['notifications'] so one `notification`
  // change event refreshes the bell, the inbox and the preference matrix together.
  notifications: (f: NotificationFilters = {}) => ['notifications', f] as const,
  notificationsUnread: ['notifications', 'unread'] as const,
  notificationPrefs: ['notifications', 'preferences'] as const,
};

export interface NotificationFilters {
  unread?: boolean;
  category?: string;
  project?: string;
}

const P = (key: string) => `/api/v1/projects/${seg(key)}`;

/* ------------------------------------------------------------------ session */

export const useMe = () => useQuery({ queryKey: qk.me, queryFn: () => api.get<MeResponse>('/api/v1/me'), staleTime: 60_000 });
export const useMeta = () => useQuery({ queryKey: qk.meta, queryFn: () => api.get<MetaDTO>('/api/v1/meta', { allowUnauthenticated: true }), staleTime: Infinity });
export const useOverview = () => useQuery({ queryKey: qk.overview, queryFn: () => api.get<OverviewDTO>('/api/v1/overview') });

/* ----------------------------------------------------------------- projects */

export const useProjects = () => useQuery({ queryKey: qk.projects, queryFn: () => api.get<ProjectDTO[]>('/api/v1/projects') });
export const useProject = (key: string) => useQuery({ queryKey: qk.project(key), queryFn: () => api.get<ProjectDTO>(P(key)) });
export const useBranches = (key: string, enabled = true) =>
  useQuery({ queryKey: qk.branches(key), queryFn: () => api.get<BranchDTO[]>(`${P(key)}/branches`), enabled, staleTime: 30_000 });
export const useEnvironments = (key: string, enabled = true) => useQuery({ queryKey: qk.environments(key), queryFn: () => api.get<EnvironmentDTO[]>(`${P(key)}/environments`), enabled });
export const useProjectTargets = (key: string, enabled = true) => useQuery({ queryKey: qk.targets(key), queryFn: () => api.get<TargetDTO[]>(`${P(key)}/targets`), enabled });
export const useReleases = (key: string, enabled = true) => useQuery({ queryKey: qk.releases(key), queryFn: () => api.get<ReleaseDTO[]>(`${P(key)}/releases`), enabled });
export const useProjectJobs = (key: string) => useQuery({ queryKey: qk.projectJobs(key), queryFn: () => api.get<JobSummaryDTO[]>(`${P(key)}/jobs`, { query: { limit: 25 } }) });
export const useManifest = (key: string, enabled = true) =>
  useQuery({ queryKey: qk.manifest(key), queryFn: () => api.get<{ manifest: Manifest | null }>(`${P(key)}/manifest`), enabled, staleTime: 60_000 });
export const useEnvConfig = (key: string, env: string | undefined) =>
  useQuery({ queryKey: qk.config(key, env ?? ''), queryFn: () => api.get<EnvironmentConfigDTO>(`${P(key)}/environments/${seg(env!)}/config`), enabled: Boolean(env) });

/* ------------------------------------------------------------------ targets */

/** Every deployment the user can see, across projects (the Deployments section). */
export const useDeployments = (f: DeploymentFilters = {}) =>
  useQuery({
    queryKey: qk.deployments(f),
    queryFn: () => api.get<TargetDTO[]>('/api/v1/targets', { query: { project: f.project, vps: f.vps, environment: f.environment, all: f.all ? '1' : undefined } }),
    placeholderData: keepPreviousData,
  });

export const useTarget = (id: string, enabled = true) => useQuery({ queryKey: qk.target(id), queryFn: () => api.get<TargetDTO>(`/api/v1/targets/${seg(id)}`), enabled });

/**
 * Records that the user opened one of a deployment's endpoints, so the Visit picker can offer
 * it first next time. Fire-and-forget: a failure must never get in the way of opening the app.
 *
 * `keepalive`, because in a desktop app window the very same click takes this window to the
 * host: an ordinary request would be cancelled at unload and the visit never counted.
 */
export const useRecordVisit = () =>
  useApiMutation((v: { targetId: string; url: string }) => api.post<null>(`/api/v1/targets/${seg(v.targetId)}/visits`, { url: v.url }, { keepalive: true }), {
    invalidate: (v) => [qk.target(v.targetId), ['deployments']],
  });
export const useBackups = (id: string, opts: { enabled?: boolean; refetchInterval?: number | false } = {}) =>
  useQuery({ queryKey: qk.backups(id), queryFn: () => api.get<BackupDTO[]>(`/api/v1/targets/${seg(id)}/backups`), enabled: opts.enabled ?? true, refetchInterval: opts.refetchInterval });

/** Runtime logs come from the agent; not tied to the change feed (key outside ['target']). */
export const useTargetLogs = (id: string, opts: { service: string; tail: number; follow: boolean; enabled: boolean }) =>
  useQuery({
    queryKey: ['target-logs', id, opts.service, opts.tail],
    queryFn: ({ signal }) => api.get<LogsResult>(`/api/v1/targets/${seg(id)}/logs`, { query: { service: opts.service || undefined, tail: opts.tail }, signal }),
    enabled: opts.enabled,
    refetchInterval: opts.follow ? 3000 : false,
    retry: false,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

/* --------------------------------------------------------------------- jobs */

export const useJobs = (f: JobFilters) =>
  useQuery({
    queryKey: qk.jobs(f),
    queryFn: () =>
      api.get<JobSummaryDTO[]>('/api/v1/jobs', { query: { active: f.active, kind: f.kind, state: f.state, target: f.target, vps: f.vps, project: f.project, limit: f.limit ?? 50 } }),
    placeholderData: keepPreviousData,
  });
/** `poll`: refetch every 3 s until the job is terminal (a fallback next to the change feed). */
export const useJob = (id: string, opts: { enabled?: boolean; poll?: boolean } = {}) =>
  useQuery({
    queryKey: qk.job(id),
    queryFn: () => api.get<JobDTO>(`/api/v1/jobs/${seg(id)}`),
    enabled: opts.enabled ?? true,
    refetchInterval: opts.poll ? (q) => (q.state.data && isTerminalJobState(q.state.data.state) ? false : 3000) : false,
  });
/** Child jobs of a parent job (e.g. the transfer jobs of a migration). */
export const useChildJobs = (parentJobId: string | null | undefined) =>
  useQuery({
    queryKey: qk.childJobs(parentJobId ?? ''),
    queryFn: () => api.get<JobSummaryDTO[]>('/api/v1/jobs', { query: { parent: parentJobId, limit: 100 } }),
    enabled: Boolean(parentJobId),
  });
export const fetchJobLogs = (id: string, after = 0) => api.get<JobLogDTO[]>(`/api/v1/jobs/${seg(id)}/logs`, { query: { after } });

/* --------------------------------------------------------------- migrations */

const MIGRATION_LIVE = new Set(['pending', 'running']);

export const useMigrations = (f: MigrationFilters = {}) =>
  useQuery({
    queryKey: qk.migrations(f),
    queryFn: () => api.get<{ migrations: MigrationDTO[] }>('/api/v1/migrations', { query: { project: f.project, state: f.state } }).then((r) => r.migrations),
    placeholderData: keepPreviousData,
    // Change events refresh the list; polling is only a fallback while something runs.
    refetchInterval: (q) => (q.state.data?.some((m) => MIGRATION_LIVE.has(m.state)) ? 10_000 : false),
  });
export const useMigration = (id: string) =>
  useQuery({
    queryKey: qk.migration(id),
    queryFn: () => api.get<MigrationDTO>(`/api/v1/migrations/${seg(id)}`),
    refetchInterval: (q) => (q.state.data && MIGRATION_LIVE.has(q.state.data.state) ? 5_000 : false),
  });
export const previewMigration = (req: MigrationRequest, signal?: AbortSignal) => api.post<MigrationPlanDTO>('/api/v1/migrations/preview', req, { signal });
export const submitMigration = (req: MigrationSubmitRequest, idempotencyKey: string) =>
  api.post<{ migration: MigrationDTO; job: JobDTO }>('/api/v1/migrations', req, { headers: { 'Idempotency-Key': idempotencyKey } });
export const cancelMigration = (id: string) => api.post<{ migration: MigrationDTO }>(`/api/v1/migrations/${seg(id)}/cancel`);
export const abandonMigration = (id: string) => api.post<{ migration: MigrationDTO }>(`/api/v1/migrations/${seg(id)}/abandon`);

/** Response of POST /targets/:id/backups (older servers: `backupId`; newer: `backup`). */
export interface BackupCreateResponse {
  job: JobDTO;
  backupId?: string;
  backup?: BackupDTO;
}
export const createBackup = (targetId: string, body: { database?: string; purpose?: 'pre-migration' }) =>
  api.post<BackupCreateResponse>(`/api/v1/targets/${seg(targetId)}/backups`, body);
/** The id of the backup a create call started, whatever the response shape. */
export function createdBackupId(r: BackupCreateResponse): string | undefined {
  const fromParams = r.job?.params?.backupId;
  return r.backupId ?? r.backup?.id ?? (typeof fromParams === 'string' ? fromParams : undefined);
}

/* ---------------------------------------------------------------------- vps */

export const useVpsList = () => useQuery({ queryKey: qk.vpsList, queryFn: () => api.get<VpsDTO[]>('/api/v1/vps') });
export const useVps = (id: string) => useQuery({ queryKey: qk.vps(id), queryFn: () => api.get<VpsDTO>(`/api/v1/vps/${seg(id)}`) });
/** Shell sessions on a VPS, newest first (platform admins only). */
export const useShellSessions = (vpsId: string, opts: { active: boolean; limit: number; enabled?: boolean }) =>
  useQuery({
    queryKey: qk.shellSessions(vpsId, opts.active),
    queryFn: () => api.get<ShellSessionDTO[]>('/api/v1/shell-sessions', { query: { vpsId, active: opts.active ? 'true' : undefined, limit: opts.limit } }),
    enabled: opts.enabled ?? true,
  });

/* -------------------------------------------------------------------- admin */

export const useSharedValues = (enabled = true) => useQuery({ queryKey: qk.shared, queryFn: () => api.get<SharedValueDTO[]>('/api/v1/shared-values'), enabled });
export const useSettings = () => useQuery({ queryKey: qk.settings, queryFn: () => api.get<PlatformSettingsDTO>('/api/v1/settings') });
export const useSessions = () => useQuery({ queryKey: qk.sessions, queryFn: () => api.get<SessionDTO[]>('/api/v1/auth/sessions') });

export const AUDIT_PAGE = 100;
export const useAudit = (f: AuditFilters) =>
  useInfiniteQuery({
    queryKey: qk.audit(f),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.get<AuditEventDTO[]>('/api/v1/audit', { query: { project: f.project, action: f.action, actor: f.actor, vps: f.vps, limit: AUDIT_PAGE, before: pageParam } }),
    getNextPageParam: (last) => (last.length >= AUDIT_PAGE ? last[last.length - 1]?.ts : undefined),
  });

/* ---------------------------------------------------------------- mutations */

/**
 * A mutation that invalidates the given query keys on success. Errors are returned to the
 * caller (rendered inline with ErrorAlert, next to the action that failed).
 */
export function useApiMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>, opts: { invalidate?: QueryKey[] | ((vars: TVars, result: TResult) => QueryKey[]) } = {}) {
  const qc = useQueryClient();
  return useMutation<TResult, ApiError | Error, TVars>({
    mutationFn: fn,
    onSuccess: (result, vars) => {
      const keys = typeof opts.invalidate === 'function' ? opts.invalidate(vars, result) : (opts.invalidate ?? []);
      for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
    },
  });
}
